import {
  generateSafeFileName,
  sanitizeHTML,
  validateText,
  validateNumber,
  validateDate,
  validateEmail
} from '../lib/validation';
import validationDefault from '../lib/validation';
import persistenceService from '../lib/persistenceService';

// Function to generate random 5-character code (alphanumeric)
export const generateRandomCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 5; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Helper function to decode HTML entities in filenames
// This fixes any existing filenames that were incorrectly encoded
const decodeHTMLEntities = (text) => {
  if (!text) return text;
  
  const entityMap = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#x27;': "'",
    '&#39;': "'",
    '&#x2F;': '/',
    '&#x60;': '`',
    '&#x3D;': '='
  };
  
  let decoded = text;
  for (const [entity, char] of Object.entries(entityMap)) {
    decoded = decoded.split(entity).join(char);
  }
  
  return decoded;
};

// Function to map database record to app row format
export const mapDbRecordToRow = (dbRecord) => {
  // employee_name already contains the full_name
  const employeeFullName = dbRecord.employee_name || '';
  
  const mappedRow = {
    id: dbRecord.id, // Use the actual database ID
    code: dbRecord.code || '',
    device: dbRecord.devices || '',
    client: dbRecord.client || '',
    page: dbRecord.page || '',
    section: dbRecord.section || '',
    usage: dbRecord.usage || '',
    description: dbRecord.description || '',
    first: dbRecord.first || '',
    last: dbRecord.last || '',
    width: dbRecord.width || '',
    height: dbRecord.height || '',
    date: dbRecord.date || '',
    employee: employeeFullName, // Map user_code back to full_name for UI
    fileName: decodeHTMLEntities(dbRecord.file_name || ''), // Decode any HTML entities
    driveLink: dbRecord.drive_url || '', // Map drive URL from database
    created: dbRecord.created_at,
    updated: dbRecord.updated_at,
    history: [{ 
      ts: dbRecord.created_at, 
      field: '_init', 
      oldValue: '', 
      newValue: 'Row loaded from database', 
      editor: 'System' 
    }],
    // Add a flag to indicate this row exists in database
    existsInDatabase: true
  };

  return mappedRow;
};

// Enhanced filename generation with comprehensive security and validation
export const generateFileName = (row, employeeMapping, options = {}) => {
  try {
    // Sanitize row data before processing
    const sanitizedRow = {};
    for (const [key, value] of Object.entries(row)) {
      if (typeof value === 'string') {
        // For filenames, don't use HTML encoding - just strip dangerous XSS patterns
        // The sanitizeFileName function will handle filesystem-safe characters
        let cleaned = String(value).trim();
        
        // Remove XSS attack patterns but don't encode entities
        // Keep apostrophes - they're safe in filenames on modern systems
        cleaned = cleaned.replace(/[<>"`]/g, ''); // Remove dangerous HTML chars; apostrophes (single quotes) are not considered dangerous and are not removed
        cleaned = cleaned.replace(/javascript:/gi, '');
        cleaned = cleaned.replace(/vbscript:/gi, '');
        cleaned = cleaned.replace(/data:/gi, '');
        
        sanitizedRow[key] = cleaned;
      } else {
        sanitizedRow[key] = value;
      }
    }
    
    // Create a modified row with user_code instead of full_name for employee
    const modifiedRow = { ...sanitizedRow };
    if (sanitizedRow.employee && employeeMapping && employeeMapping[sanitizedRow.employee]) {
      modifiedRow.employee = employeeMapping[sanitizedRow.employee];
    }
    
    // Use enhanced filename generation with security options
    const result = generateSafeFileName(modifiedRow, {
      maxLength: 200,
      separator: '___',
      includeTimestamp: false,
      priorityFields: ['code', 'client', 'page', 'section'],
      strictMode: true,
      preserveAmpersand: true,
      ...options
    });
    
    // Validate the generated filename
    if (!validationDefault.isValidFilename || !validationDefault.isValidFilename(result)) {
      console.warn('🟡 generateFileName - Generated filename failed validation, using fallback');
      return `file_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    }
    
    return result;
  } catch (error) {
    console.error('🔴 generateFileName - Error:', error);
    // Return a safe fallback filename
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 8);
    return `error_${timestamp}_${randomId}`;
  }
};

// Image compression helper (returns {blob, dataUrl})
export const compressImage = (file, maxDim = 300, quality = 0.8) => new Promise((resolve, reject) => {
  const img = new Image();
  const reader = new FileReader();
  reader.onload = e => {
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > height && width > maxDim) {
        height = Math.round(height * (maxDim / width));
        width = maxDim;
      } else if (height >= width && height > maxDim) {
        width = Math.round(width * (maxDim / height));
        height = maxDim;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => {
        if (!blob) return reject(new Error('Compression failed'));
        resolve({ blob, dataUrl: canvas.toDataURL('image/jpeg', quality) });
      }, 'image/jpeg', quality);
    };
    img.onerror = () => reject(new Error('Image load error'));
    img.src = e.target.result;
  };
  reader.onerror = () => reject(new Error('File read error'));
  reader.readAsDataURL(file);
});

// Upload image to Supabase storage
export const uploadImageToStorage = async (file, fileName, namingFileId = null, session, setUploadProgressMap) => {
  try {
    if (!session?.user?.id) throw new Error('User not authenticated');
    setUploadProgressMap(p => ({ ...p, [namingFileId || fileName]: { status: 'uploading' } }));
    const result = await persistenceService.uploadFileWithMetadata(file, {
      originalName: fileName,
      fileName: fileName.replace(/\.[^.]+$/, ''),
      namingFileId
    });
    setUploadProgressMap(p => ({ ...p, [namingFileId || fileName]: { status: 'completed' } }));
    return result.url;
  } catch (error) {
    setUploadProgressMap(p => ({ ...p, [namingFileId || fileName]: { status: 'error', error: error.message } }));
    console.error('🔴 uploadImageToStorage - Error:', error);
    throw error;
  }
};

// Enhanced input validation and sanitization
export const validateFieldInput = (field, value) => {
  let sanitizedValue = value;
  let validationError = null;

  if (typeof value === 'string') {
    // For text fields, don't use HTML entity encoding at all
    // React will handle proper escaping when rendering to DOM
    // Just strip truly dangerous XSS patterns
    if (field === 'driveLink') {
      // URLs need special handling - don't HTML encode
      sanitizedValue = value.trim();
    } else {
      // For regular text fields, use centralized sanitization for XSS
      sanitizedValue = sanitizeHTML(value);
    }

    // Field-specific validation with enhanced security
    if (field === 'width' || field === 'height') {
      const numValidation = validateNumber(value, {
        required: false,
        min: 0,
        max: 99999,
        integer: true,
        fieldName: field
      });
      if (!numValidation.isValid) {
        validationError = numValidation.error;
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Invalid ${field} value:`, numValidation.error);
        }
        sanitizedValue = ''; // Clear invalid numeric values
      } else {
        sanitizedValue = numValidation.sanitized === '' ? '' : String(numValidation.sanitized);
      }
    } else if (field === 'date') {
      const dateValidation = validateDate(value, {
        required: false,
        minDate: '1900-01-01',
        maxDate: '2100-12-31',
        fieldName: 'Date'
      });
      if (!dateValidation.isValid && value !== '') {
        validationError = dateValidation.error;
        if (process.env.NODE_ENV === 'development') {
          console.warn('Invalid date value:', dateValidation.error);
        }
      }
      sanitizedValue = dateValidation.sanitized;
    } else if (field === 'email') {
      // Enhanced email validation if field is email
      const emailValidation = validateEmail(value);
      if (!emailValidation.isValid && value !== '') {
        validationError = emailValidation.error;
        if (process.env.NODE_ENV === 'development') {
          console.warn('Invalid email value:', emailValidation.error);
        }
      }
      sanitizedValue = emailValidation.sanitized;
    } else if (field === 'driveLink') {
      // URL validation for Drive Link field - don't HTML encode URLs
      if (value && value.trim() !== '') {
        // Basic URL validation
        try {
          new URL(value);
          sanitizedValue = value.trim(); // Just trim whitespace, don't HTML encode
        } catch (e) {
          // If it's not a valid URL, still allow it but warn
          sanitizedValue = value.trim();
          if (process.env.NODE_ENV === 'development') {
            console.warn('Drive Link appears to be an invalid URL:', value);
          }
        }
      } else {
        sanitizedValue = value; // Allow empty values
      }
    } else {
      // Enhanced text field validation with security measures
      const textValidation = validateText(value, {
        required: false,
        maxLength: field === 'description' ? 1000 : 500,
        allowHTML: false,
        preserveAmpersand: true,
        fieldName: field
      });
      
      if (!textValidation.isValid) {
        validationError = textValidation.error;
      }
      
      sanitizedValue = textValidation.sanitized;
      
      // Additional security check for suspicious patterns
      if (validationDefault.detectXSS && validationDefault.detectXSS(sanitizedValue)) {
        console.warn(`Potential XSS attempt detected in ${field}:`, value);
        sanitizedValue = validationDefault.removeDangerousUnicode ? validationDefault.removeDangerousUnicode(sanitizedValue) : sanitizedValue;
      }
    }
  }

  return { sanitizedValue, validationError };
};

// Static data for dropdowns
export const staticData = {
  // New dropdown options for Devices field (was previously free-text input)
  devices: [
    'Smartwatch_&_Mini_Screen',
    'Laptop_&_Desktop',
    'All_Devices',
    'Smartphone',
    'iPhone X_to_13',
    'iPhone_6_7_8',
    'iPhone_15_Pro',
    'Tablet',
    'iPad_Mini',
    'iPad_Pro',
    'Laptop',
    'Extra_Large_Screen',
    'Desktop'
  ],
  sections: ['NA', 'S01', 'S02', 'S03', 'S04', 'S05', 'S06', 'S07', 'S08', 'S09', 'S10', 'S11', 'S12', 'S13'],
  usage: ['Overlay', 'Thumbnail', 'Bio', 'Left', 'Center', 'Right', 'Top', 'Bottom', 'Horizontal', 'Vertical', 'Color', 'White', 'Black']
};